// PokerCity Notification Service
// Handles in-app notifications for dealer invite status changes

import { db } from '../../firebase';
import {
  collection, doc, addDoc, updateDoc, getDocs,
  query, where, orderBy, serverTimestamp, writeBatch,
} from 'firebase/firestore';

export type NotificationType =
  | 'dealer_invite_received'
  | 'dealer_accepted'
  | 'dealer_declined'
  | 'dealer_standby'
  | 'dealer_cancelled'        // accepted → cancelled
  | 'dealer_timed_out'
  | 'standby_activated'       // TD activated a standby dealer
  | 'standby_released'        // standby released (primary confirmed)
  | 'standby_expired'         // standby call-by time passed
  | 'event_no_dealer'         // all dealers cancelled, no backup
  | 'dealer_rated';           // TD rated the dealer

export interface PCNotification {
  id?: string;
  type: NotificationType;
  recipientUid: string;
  eventId: string;
  eventName: string;
  actorName: string;    // who triggered the notification
  message: string;
  read: boolean;
  urgent: boolean;
  createdAt: any;
  metadata?: Record<string, any>;
}

class NotificationService {

  async send(notification: Omit<PCNotification, 'id' | 'read' | 'createdAt'>): Promise<void> {
    try {
      await addDoc(collection(db, 'users', notification.recipientUid, 'notifications'), {
        ...notification,
        read: false,
        createdAt: serverTimestamp(),
      });
    } catch (e) { console.error('NotificationService.send:', e); }
  }

  async getUnread(uid: string): Promise<PCNotification[]> {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'users', uid, 'notifications'),
          where('read', '==', false),
          orderBy('createdAt', 'desc')
        )
      );
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as PCNotification));
    } catch { return []; }
  }

  async getAll(uid: string): Promise<PCNotification[]> {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'users', uid, 'notifications'),
          orderBy('createdAt', 'desc')
        )
      );
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as PCNotification));
    } catch { return []; }
  }

  async markRead(uid: string, notifId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', uid, 'notifications', notifId), { read: true });
    } catch (e) { console.error(e); }
  }

  async markAllRead(uid: string): Promise<void> {
    try {
      const unread = await this.getUnread(uid);
      const batch = writeBatch(db);
      unread.forEach(n => {
        if (n.id) batch.update(doc(db, 'users', uid, 'notifications', n.id), { read: true });
      });
      await batch.commit();
    } catch (e) { console.error(e); }
  }

  // ── CASCADE: send to all affected parties when dealer status changes ──

  async notifyDealerInvited(params: {
    dealerUid: string;
    tdName: string;
    eventId: string;
    eventName: string;
    deadline: string;
    urgent: boolean;
  }): Promise<void> {
    const hoursUntil = Math.round((new Date(params.deadline).getTime() - Date.now()) / 3600000);
    await this.send({
      type: 'dealer_invite_received',
      recipientUid: params.dealerUid,
      eventId: params.eventId,
      eventName: params.eventName,
      actorName: params.tdName,
      message: params.urgent
        ? `⚡ Urgent invite from ${params.tdName} — response needed within ${hoursUntil}hrs`
        : `${params.tdName} has invited you to deal at "${params.eventName}"`,
      urgent: params.urgent,
      metadata: { deadline: params.deadline },
    });
  }

  async notifyTDDealerResponded(params: {
    tdUid: string;
    dealerName: string;
    eventId: string;
    eventName: string;
    status: 'accepted' | 'declined' | 'standby';
    standbyCallByTime?: string;
  }): Promise<void> {
    const messages: Record<string, string> = {
      accepted: `✅ ${params.dealerName} confirmed for "${params.eventName}"`,
      declined: `❌ ${params.dealerName} declined "${params.eventName}" — find a replacement`,
      standby:  `🟡 ${params.dealerName} is on standby for "${params.eventName}"${params.standbyCallByTime ? ` — call by ${new Date(params.standbyCallByTime).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}` : ''}`,
    };
    const typeMap: Record<string, NotificationType> = {
      accepted: 'dealer_accepted',
      declined: 'dealer_declined',
      standby: 'dealer_standby',
    };
    await this.send({
      type: typeMap[params.status],
      recipientUid: params.tdUid,
      eventId: params.eventId,
      eventName: params.eventName,
      actorName: params.dealerName,
      message: messages[params.status],
      urgent: params.status === 'declined',
      metadata: { standbyCallByTime: params.standbyCallByTime },
    });
  }

  async notifyDealerCancelled(params: {
    tdUid: string;
    standbyUids: string[];
    dealerName: string;
    eventId: string;
    eventName: string;
    hoursBeforeEvent: number;
  }): Promise<void> {
    const urgent = params.hoursBeforeEvent < 24;
    // Notify TD
    await this.send({
      type: 'dealer_cancelled',
      recipientUid: params.tdUid,
      eventId: params.eventId,
      eventName: params.eventName,
      actorName: params.dealerName,
      message: urgent
        ? `⚠️ URGENT: ${params.dealerName} cancelled for "${params.eventName}" — ${params.hoursBeforeEvent}hrs before the event`
        : `⚠️ ${params.dealerName} cancelled for "${params.eventName}"`,
      urgent,
    });
    // Notify all standbys
    for (const standbyUid of params.standbyUids) {
      await this.send({
        type: 'standby_activated',
        recipientUid: standbyUid,
        eventId: params.eventId,
        eventName: params.eventName,
        actorName: params.dealerName,
        message: urgent
          ? `⚡ Primary dealer cancelled "${params.eventName}" — are you available? Respond ASAP`
          : `Primary dealer cancelled "${params.eventName}" — are you available to step in?`,
        urgent,
      });
    }
    // If no standbys, flag the event
    if (params.standbyUids.length === 0) {
      await this.send({
        type: 'event_no_dealer',
        recipientUid: params.tdUid,
        eventId: params.eventId,
        eventName: params.eventName,
        actorName: 'PokerCity',
        message: `No dealer confirmed for "${params.eventName}" — find a replacement`,
        urgent: true,
      });
    }
  }

  async notifyStandbyReleased(params: {
    standbyUid: string;
    dealerName: string; // the new confirmed dealer
    eventId: string;
    eventName: string;
  }): Promise<void> {
    await this.send({
      type: 'standby_released',
      recipientUid: params.standbyUid,
      eventId: params.eventId,
      eventName: params.eventName,
      actorName: params.dealerName,
      message: `You have been released from standby — "${params.eventName}" is now covered`,
      urgent: false,
    });
  }

  async notifyDealerTimedOut(params: {
    tdUid: string;
    dealerUid: string;
    dealerName: string;
    eventId: string;
    eventName: string;
  }): Promise<void> {
    // Notify TD
    await this.send({
      type: 'dealer_timed_out',
      recipientUid: params.tdUid,
      eventId: params.eventId,
      eventName: params.eventName,
      actorName: params.dealerName,
      message: `⏰ ${params.dealerName} did not respond to your invite for "${params.eventName}"`,
      urgent: true,
    });
  }
}

export default NotificationService;
